/*global zE:true*/
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import config from 'linced-app/config/environment';

const SCRIPT_ID = 'ze-snippet';

// lazily loads the zendesk widget and tracks whether it is open.
export default class ZendeskService extends Service {
  @service
  logging;

  @service
  session;

  @tracked
  isOpen = false;

  scriptUrl = config.zendeskKey
    ? `https://static.zdassets.com/ekr/snippet.js?key=${config.zendeskKey}`
    : null;

  _loadPromise = null;

  // injects the zendesk snippet once and resolves when `zE` is ready.
  load() {
    if (!this.scriptUrl) {
      return Promise.reject(new Error('Zendesk is not configured'));
    }
    if (this._loadPromise) {
      return this._loadPromise;
    }

    this._loadPromise = new Promise((resolve, reject) => {
      // must be set before the snippet loads.
      window.zESettings = {
        webWidget: {
          position: { vertical: 'top' },
          offset: { vertical: '64px' },
          color: { theme: '#01667d' },
          contactForm: {
            suppress: true,
            fields: [
              {
                id: 360035790314,
                prefill: { '*': this.session.school?.name ?? '' },
              },
            ],
          },
        },
      };

      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = this.scriptUrl;
      script.async = true;
      script.addEventListener(
        'load',
        () => {
          // Hide the floating launcher; we have our own help button.
          zE('webWidget', 'hide');
          // tracks closes made from inside the widget.
          zE('webWidget:on', 'close', () => this._onClose());
          resolve();
        },
        { once: true },
      );
      script.addEventListener(
        'error',
        () => {
          // Don't cache a failure, so the next click can retry.
          this._loadPromise = null;
          script.remove();
          reject(new Error(`Failed to load Zendesk from ${this.scriptUrl}`));
        },
        { once: true },
      );
      document.head.appendChild(script);
    });

    return this._loadPromise;
  }

  // loads the widget if needed, then opens it prefilled.
  async open({ suppressContactForm = true, name = '', email = '' } = {}) {
    this.isOpen = true;
    try {
      await this.load();
    } catch (error) {
      this.isOpen = false;
      this.logging.pushError(error, 'zendesk:open');
      return;
    }
    if (!this.isOpen) {
      // Closed again while the script was still loading.
      return;
    }

    // Update help article suggestions based off current URL.
    zE('webWidget', 'helpCenter:setSuggestions', { url: true });
    zE('webWidget', 'updateSettings', {
      webWidget: {
        contactForm: {
          suppress: suppressContactForm,
        },
      },
    });
    zE('webWidget', 'prefill', {
      name: { value: name },
      email: { value: email },
    });
    // Show and open the widget (we keep it hidden when closed).
    zE('webWidget', 'show');
    zE('webWidget', 'open');
  }

  close() {
    if (!this.isOpen) {
      return;
    }
    if (window.zE) {
      // Calls the close handler registered in `load`.
      zE('webWidget', 'close');
    } else {
      this.isOpen = false;
    }
  }

  _onClose() {
    this.isOpen = false;
    zE('webWidget', 'hide');
  }
}
